/**
 *
 * CheckIn
 *
 * Public page reached by clicking the link in a six-monthly check-in
 * email. Calls the API to stamp the response, then shows the result.
 * No auth, no slice — single useEffect that hits apiRequest. See API
 * ADR-0002.
 *
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { apiRequest } from 'utils/request';

type Status = 'loading' | 'success' | 'not_found' | 'error';

export function CheckIn() {
  const { key } = useParams<{ key: string }>();
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!key) {
      setStatus('not_found');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await apiRequest({
          url: `confirm-interest/${key}`,
          method: 'get',
        });
        if (!cancelled) setStatus('success');
      } catch (e: any) {
        if (cancelled) return;
        if (e?.payload?.code === 404) setStatus('not_found');
        else setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  const title =
    status === 'success'
      ? 'Thank you'
      : status === 'not_found'
      ? 'Link not recognised'
      : status === 'error'
      ? 'Something went wrong'
      : 'Recording your response…';

  return (
    <>
      <Helmet>
        <title>{title} — TVG Voices</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'grey.100',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4 }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              {status === 'loading' && <CircularProgress />}
              <Typography variant="h4" color="primary">
                {title}
              </Typography>
              {status === 'success' && (
                <>
                  <Typography variant="body1">
                    We've recorded that you'd like to stay on The Voiceover
                    Gallery's database. No further action is needed.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You can close this tab.
                  </Typography>
                </>
              )}
              {status === 'not_found' && (
                <Typography variant="body1">
                  We couldn't find this check-in. The link may be out of date or
                  already used — but if you've recently confirmed via another
                  email there's nothing more to do.
                </Typography>
              )}
              {status === 'error' && (
                <Typography variant="body1">
                  We couldn't record your response just now. Please try the link
                  again in a moment.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
